import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Phone, Loader2, ArrowLeft } from "lucide-react";
import { signUp } from "../../services/auth";
import Stepper, { Step } from "../../components/signO/ui/Stepper";
import Hyperspeed from "../../components/signO/ui/Hyperspeed";
import { hyperspeedPresets } from "../../components/signO/ui/Hyperspeed";

const Signup = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async () => {
    if (formData.password !== formData.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.email ||
      !formData.password
    ) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Prepare data for API (remove confirm_password)
      const { confirm_password, ...apiData } = formData;
      await signUp(apiData);
      // Redirect to login or show success message
      navigate("/login");
    } catch (err) {
      setError(
        err.response?.data?.detail || "Signup failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <Hyperspeed
          effectOptions={{
            ...hyperspeedPresets.two,
            colors: {
              ...hyperspeedPresets.two.colors,
              background: 0x000000,
              rightCars: [0x5227ff, 0x3b82f6, 0x60a5fa],
              leftCars: [0xff9ffc, 0xec4899, 0xf472b6],
            },
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card w-full max-w-2xl p-8 relative z-10 my-8"
      >
        <Link
          to="/"
          className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors z-20"
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>

        <div className="text-center mb-6 mt-4">
          <h2 className="text-3xl font-bold text-white mb-2">Create Account</h2>
          <p className="text-gray-400">
            Join SignO for secure digital signatures
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-3 rounded-lg mb-6 text-sm text-center">
            {error}
          </div>
        )}

        <Stepper
          initialStep={1}
          onFinalStepCompleted={handleSignup}
          backButtonText="Back"
          nextButtonText="Next"
          stepCircleContainerClassName="bg-transparent shadow-none border-none"
          stepContainerClassName="mb-8"
          contentClassName="min-h-[300px]"
          footerClassName="mt-4 flex justify-end items-center gap-4"
        >
          <Step>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4 text-center">
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      name="first_name"
                      required
                      value={formData.first_name}
                      onChange={handleChange}
                      className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      name="last_name"
                      required
                      value={formData.last_name}
                      onChange={handleChange}
                      className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Step>
          <Step>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4 text-center">
                Contact Details
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number{" "}
                  <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
            </div>
          </Step>
          <Step>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4 text-center">
                Secure your account
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="password"
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="password"
                    name="confirm_password"
                    required
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                    placeholder="••••••••"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSignup();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </Step>
        </Stepper>

        {loading && (
          <div className="absolute inset-0 bg-dark/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-2xl">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        )}

        <p className="mt-8 text-center text-gray-400 text-sm relative z-20 pb-2">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-primary hover:text-primary/80 font-medium"
          >
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Signup;
